/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import { createContext } from 'react'

import { jwtDecode } from 'jwt-decode'

import { getStandardDateTime } from '@/utils/getStandardDateTime'

import type { AuthContextValue, UserInfo, AppJwtPayload } from './auth-types'

/**
 * State, reducer and token helpers shared by the two auth strategies.
 *
 * `AuthProvider` signs in through Auth0; `GeotabAuthProvider` signs in through a MyGeotab
 * add-in session. Both end up holding the same app JWT and publish the same context, so
 * everything downstream of `useAuth()` is unaware of which one is mounted.
 */

// --- State & Reducer ---

export interface AuthState {
  token: string | null
  user: UserInfo | null
  originalUser: UserInfo | null
  permissions: string[]
  /** Enabled-insight slugs from the token; undefined = unrestricted. */
  insights: string[] | undefined
  isLoading: boolean
  isInitialized: boolean
  needsHardLogin: boolean
  error: string | null
  /** Signed in fine, but no active Insights profile — show request-access, not an error. */
  noAccess: boolean
}

export type AuthAction =
  | {
      type: 'SET_AUTHENTICATED'
      token: string
      user: UserInfo
      originalUser: UserInfo | null
      permissions: string[]
      insights: string[] | undefined
    }
  | { type: 'SET_LOADING'; isLoading: boolean }
  | { type: 'SET_INITIALIZED_ERROR'; error: string }
  | { type: 'SET_NEEDS_HARD_LOGIN'; needsHardLogin: boolean }
  | { type: 'SET_NO_ACCESS' }
  | { type: 'CLEAR' }

export const initialState: AuthState = {
  token: null,
  user: null,
  originalUser: null,
  permissions: [],
  insights: undefined,
  isLoading: true,
  isInitialized: false,
  needsHardLogin: false,
  error: null,
  noAccess: false,
}

export function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_AUTHENTICATED':
      return {
        ...state,
        token: action.token,
        user: action.user,
        originalUser: action.originalUser,
        permissions: action.permissions,
        insights: action.insights,
        isLoading: false,
        isInitialized: true,
        error: null,
      }
    case 'SET_LOADING':
      return { ...state, isLoading: action.isLoading }
    case 'SET_INITIALIZED_ERROR':
      return { ...state, isLoading: false, isInitialized: true, error: action.error }
    case 'SET_NEEDS_HARD_LOGIN':
      return { ...state, needsHardLogin: action.needsHardLogin }
    case 'SET_NO_ACCESS':
      return { ...state, isLoading: false, isInitialized: true, noAccess: true }
    case 'CLEAR':
      return { ...initialState, isLoading: false, isInitialized: true }
    default:
      return state
  }
}

// --- Helpers ---

type UserMetadata = NonNullable<AppJwtPayload['user_metadata']>

/**
 * Map Auth0 user_metadata to our UserInfo.
 *
 * The Auth0 path reads the metadata out of the access token; the Geotab path has no Auth0
 * token to decode and gets the same metadata straight from the backend. Sharing this keeps
 * the two from drifting.
 */
export function buildUserInfo(meta: UserMetadata, sub: string, profileImage?: string): UserInfo {
  return {
    id: sub || '',
    firstName: meta.first_name || '',
    lastName: meta.last_name || '',
    email: meta.email || '',
    mobile: meta.mobile_number || '',
    username: meta.user_name || '',
    company: meta.company_id || '',
    timezone: meta.timeZoneId || '',
    profileImage: profileImage || '',
    refreshUserId: meta.id,
    defaultDistanceUnits: meta.defaultDistanceUnits || 'Metric',
    defaultVolumeUnits: meta.default_volume_units || 'Metric',
    defaultWeightUnits: meta.default_weight_units || 'Metric',
    defaultTemperatureUnits: meta.default_temperature_units || 'Metric',
    createdTime: meta.created_time ? getStandardDateTime(meta.created_time) : '',
    lastLoginTime: meta.last_login_time ? getStandardDateTime(meta.last_login_time) : '',
    lastActivityTime: meta.last_activity_time ? getStandardDateTime(meta.last_activity_time) : '',
  }
}

export function extractUserFromAuth0Token(token: string, profileImage?: string): UserInfo | null {
  try {
    const decoded = jwtDecode<AppJwtPayload>(token)
    const meta = decoded.user_metadata
    if (!meta) return null
    return buildUserInfo(meta, decoded.sub || '', profileImage)
  } catch {
    return null
  }
}

export function extractPermissionsFromToken(token: string): string[] {
  try {
    const decoded = jwtDecode<AppJwtPayload>(token)
    return decoded.permissions || []
  } catch {
    return []
  }
}

/**
 * The token's `insights` claim. Absent (or an undecodable token) means unrestricted, which
 * is the pre-permissions behaviour every existing user has.
 */
export function extractInsightsFromToken(token: string): string[] | undefined {
  try {
    const decoded = jwtDecode<AppJwtPayload>(token)
    return Array.isArray(decoded.insights) ? decoded.insights : undefined
  } catch {
    return undefined
  }
}

export function detectSudo(auth0User: UserInfo, appToken: string): { user: UserInfo; originalUser: UserInfo | null } {
  try {
    const appClaims = jwtDecode<AppJwtPayload>(appToken)
    if (appClaims.email && auth0User.email && appClaims.email !== auth0User.email) {
      const nameParts = (appClaims.displayName || '').split(' ')
      const sudoUser: UserInfo = {
        ...auth0User,
        id: appClaims.auth0Id || auth0User.id,
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: appClaims.email,
        username: appClaims.userName || '',
        timezone: appClaims.timezone || auth0User.timezone,
      }
      // Use originalAuth0Id from the app token — this is the real user's ID
      // as known by the backend (matches the profile uuid in the DB)
      const originalUser: UserInfo = {
        ...auth0User,
        id: appClaims.originalAuth0Id || auth0User.id,
      }
      return { user: sudoUser, originalUser }
    }
  } catch {
    // ignore decode errors
  }
  return { user: auth0User, originalUser: null }
}

// --- Context ---

export const AuthContext = createContext<AuthContextValue | null>(null)
